import React, { useState, useEffect } from "react";
import { JsonRpc } from "@proton/js";
import { Info, Search } from "lucide-react";
import Tooltip from "./Tooltip";
import Loader from "./loader/Loader";
import NoMarketsFound from "./NoMarketsFound";
import FilterDropdown from "./FilterDropdown";
import { normalizeTimestamp } from "../utils/dateUtils";
import {
  getMultiOutcomeRowPrices,
  buildMultiOutcomeStates,
} from "../utils/markets";
import {
  computeNetInvested,
  grossCostBasisMicro,
  normalizeMoynStateRows,
  summarizeMoynPositions,
} from "../utils/moyn";
import MarketsCard, { type Market, type Outcome } from "./MarketsCard";

interface MarketsListProps {
  session: any;
  onSelectMarket: (id: number) => void;
}

const MarketsList: React.FC<MarketsListProps> = ({
  session,
  onSelectMarket,
}) => {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [copiedMarketId, setCopiedMarketId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "expiry" | "popular">(
    "newest",
  );
  const [watchlist, setWatchlist] = useState<number[]>([]);

  useEffect(() => {
    fetchMarkets();
    const interval = setInterval(fetchMarkets, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    const saved = localStorage.getItem("watchlist");
    if (saved) {
      try {
        setWatchlist(JSON.parse(saved));
      } catch (error) {
        console.error("Error loading watchlist:", error);
      }
    }
  }, []);

  const toggleWatchlist = (marketId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newWatchlist = watchlist.includes(marketId)
      ? watchlist.filter((id) => id !== marketId)
      : [...watchlist, marketId];
    setWatchlist(newWatchlist);
    localStorage.setItem("watchlist", JSON.stringify(newWatchlist));
  };

  const fetchMarkets = async () => {
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const result = await rpc.get_table_rows({
        code: contractName,
        scope: contractName,
        table: "markets3",
        lower_bound: "0",
        limit: 200,
        reverse: true,
      });

      // Only show markets that are live (include expired for "All" tab)
      const liveRows = result.rows.filter((row: any) => {
        const isLive = row.isLive === true || row.isLive === 1;
        return isLive;
      });

      const userActor =
        session && session.auth && session.auth.actor
          ? session.auth.actor.toString()
          : null;
      const normalizedMarkets = await Promise.all(
        liveRows.map(async (row: any) => {
          const market: Market = {
            ...row,
            category: row.category || "General",
            expireSec: normalizeTimestamp(row.expire),
            outcomes_count: row.outcomes_count || 2,
            resolved: !!row.resolved,
            version: row.version || 0,
            q_yes: row.q_yes || 0,
            q_no: row.q_no || 0,
            b: row.b || 500_000_000, // Default b = 500 * SCALE
            is_refundable: !!row.is_refundable,
            fee_bps: row.fee_bps ?? 100,
            total_collateral_in: row.total_collateral_in || 0,
            created_at: row.created_at
              ? normalizeTimestamp(row.created_at)
              : undefined,
          };

          // Calculate LMSR odds for version >= 2 markets
          if (
            market.version &&
            market.version >= 2 &&
            market.b &&
            market.b > 0
          ) {
            const SCALE = 1_000_000;
            const b = market.b / SCALE;

            if (market.version === 3) {
              // V3: fetch per-outcome state from moynstate
              try {
                const moynStateResult = await rpc.get_table_rows({
                  code: contractName,
                  scope: row.id.toString(),
                  table: "moynstate",
                  limit: 100,
                });
                market.outcome_states = normalizeMoynStateRows(
                  moynStateResult.rows,
                );
              } catch (error) {
                console.error(
                  `Error fetching moynstate for market ${row.id}:`,
                  error,
                );
              }
              // V3 odds are calculated per outcome below after outcomes are fetched
              market.yesOdds = 50;
              market.noOdds = 50;
            } else {
              // V2 binary: market-level q_yes/q_no
              const qYes = (market.q_yes || 0) / SCALE;
              const qNo = (market.q_no || 0) / SCALE;

              const expYes = Math.exp(qYes / b);
              const expNo = Math.exp(qNo / b);
              const total = expYes + expNo;

              market.yesOdds = Math.round((expYes / total) * 100);
              market.noOdds = Math.round((expNo / total) * 100);
            }
          } else {
            // Default to 50/50 for non-LMSR markets or markets with no activity
            market.yesOdds = 50;
            market.noOdds = 50;
          }

          if (market.outcomes_count > 0) {
            try {
              const outcomesResult = await rpc.get_table_rows({
                code: contractName,
                scope: row.id.toString(),
                table: "outcomes",
                limit: 100,
              });

              const outcomes: Outcome[] = outcomesResult.rows.map(
                (outcomeRow: any) => {
                  let probability = 100 / market.outcomes_count;
                  if (
                    market.version === 3 &&
                    market.outcome_states &&
                    market.b &&
                    market.b > 0
                  ) {
                    const rowPrices = getMultiOutcomeRowPrices(
                      buildMultiOutcomeStates(
                        outcomesResult.rows.map((row: any) => row.outcome_id),
                        market.outcome_states,
                      ),
                      market.b,
                    );
                    probability = Math.round(
                      (rowPrices[outcomeRow.outcome_id]?.yesPrice ??
                        1 / market.outcomes_count) * 100,
                    );
                  } else if (market.version === 2 && market.b && market.b > 0) {
                    // V2 binary: use market-level q_yes/q_no
                    probability =
                      outcomeRow.outcome_id === 0
                        ? (market.yesOdds ?? 50)
                        : (market.noOdds ?? 50);
                  }
                  return {
                    outcome_id: outcomeRow.outcome_id,
                    name: outcomeRow.name,
                    display_order: outcomeRow.display_order,
                    probability,
                  };
                },
              );

              outcomes.sort((a, b) => a.display_order - b.display_order);
              market.outcomes = outcomes;
            } catch (error) {
              console.error(
                `Error fetching outcomes for market ${row.id}:`,
                error,
              );
              market.outcomes = [];
            }
          }

          // For LMSR markets (version >= 2), use total_collateral_in from market data
          // For legacy markets, calculate from orders
          if (row.version >= 2) {
            // LMSR market: net volume = buys minus sells (a user cannot sell more than they bought)
            market.totalInvested = Math.ceil(
              Math.max(
                0,
                (row.total_collateral_in || 0) -
                  (row.total_collateral_out || 0),
              ) / 1_000_000,
            );
            

            // Count unique participants from the position table
            try {
              const posTable = market.version === 3 ? "moynpos" : "poslmsr";
              const lmsrPosResult = await rpc.get_table_rows({
                code: contractName,
                scope: row.id.toString(),
                table: posTable,
                limit: 100,
              });
              if (market.version === 3) {
                const summary = summarizeMoynPositions(
                  lmsrPosResult.rows,
                  userActor ?? null,
                  market.fee_bps ?? 100,
                );
                market.participants = summary.participants;
                

                market.myInvested = summary.netInvested;
              } else {
                market.participants = market.resolved ? lmsrPosResult.rows.length : lmsrPosResult.rows.filter((row) => row.shares_yes > 0 || row.shares_no > 0).length
 
                if (userActor) {
                  const userRow = lmsrPosResult.rows.find(
                    (r: any) => r.user === userActor,
                  );
                  const spent = userRow ? grossCostBasisMicro(userRow) : 0;
                  market.myInvested = computeNetInvested(spent);
                }
              }
            } catch (error) {
              console.error(
                `Error fetching LMSR positions for market ${row.id}:`,
                error,
              );
              market.participants = 0;
            }
          } else {
            // Legacy order-book market: calculate from orders
            try {
              const ordersResult = await rpc.get_table_rows({
                code: contractName,
                scope: row.id.toString(),
                table: "orders",
                limit: 1000,
              });

              // Count unique accounts from orders
              const uniqueAccounts = new Set(
                ordersResult.rows.map((order: any) => order.account),
              );
              market.participants = uniqueAccounts.size;

              // Calculate total invested (sum of price * quantity for all bid orders)
              let totalInvested = 0;
              ordersResult.rows.forEach((order: any) => {
                if (order.isBid) {
                  // Price is stored as integer USDTEST
                  const price =
                    typeof order.price === "number"
                      ? order.price
                      : parseFloat(order.price) || 0;
                  const quantity = order.quantity || 0;
                  totalInvested += price * quantity;
                }
              });
              market.totalInvested = totalInvested;
            } catch (error) {
              console.error(
                `Error fetching orders for market ${row.id}:`,
                error,
              );
              market.participants = 0;
              market.totalInvested = 0;
            }
          }

          return market;
        }),
      );

      setMarkets(normalizedMarkets);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching markets:", error);
      setLoading(false);
    }
  };

  const nowSec = Math.floor(Date.now() / 1000);

  const filteredMarkets = markets
    .filter((market) => {
      const isExpired = market.expireSec > 0 && market.expireSec < nowSec;
      if (filter === "active") return !market.resolved && !isExpired;
      if (filter === "resolved") return market.resolved;
      if (filter === "watchlist") return watchlist.includes(market.id);
      return true; // 'all' tab shows everything including expired
    })
    .filter((market) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const question = (market.question || "").toLowerCase();
      const category = (market.category || "").toLowerCase();
      return question.includes(query) || category.includes(query);
    })
    .filter((market) => {
      if (!categoryFilter) return true;
      return market.category === categoryFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest": {
          const aExpired = a.expireSec > 0 && a.expireSec < nowSec;
          const bExpired = b.expireSec > 0 && b.expireSec < nowSec;

          // Non-expired markets first
          if (aExpired !== bExpired) {
            return aExpired ? 1 : -1;
          }

          // Then sort by newest id
          return b.id - a.id;
        }
        case "expiry":
          return a.expireSec - b.expireSec;
        case "popular":
          return 0;
        default:
          return 0;
      }
    });

  const categories = Array.from(
    new Set(markets.map((m) => m.category).filter(Boolean)),
  ).sort();

  const categoryOptions = [
    { value: "", label: "All Categories" },
    ...categories.map((cat) => ({
      value: cat,
      label: cat,
    })),
  ];

  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "expiry", label: "Expiring Soon" },
    { value: "popular", label: "Most Popular" },
  ];

  const getMarketUrl = (marketId: number) => {
    return `${window.location.origin}/market/${marketId}`;
  };

  // Get share URL that uses share.php for proper OG meta tags on social media
  const getShareUrl = (marketId: number) => {
    return `${window.location.origin}/share.php?market=${marketId}`;
  };

  const handleShare = (
    marketId: number,
    platform: "twitter" | "facebook" | "copy",
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    const market = markets.find((m) => m.id === marketId);
    const text = market
      ? `Check out this prediction market: ${market.question}`
      : "Check out this prediction market";

    switch (platform) {
      case "twitter":
        // Use share.php URL for Twitter to get proper OG image preview
        const twitterUrl = getShareUrl(marketId);
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(twitterUrl)}`,
          "_blank",
        );
        break;
      case "facebook":
        // Use share.php URL for Facebook to get proper OG image preview
        const fbUrl = getShareUrl(marketId);
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fbUrl)}`,
          "_blank",
        );
        break;
      case "copy":
        // For copy, use direct market URL (user will paste in browser)
        const copyUrl = getMarketUrl(marketId);
        navigator.clipboard.writeText(copyUrl).then(() => {
          setCopiedMarketId(marketId);
          setTimeout(() => setCopiedMarketId(null), 2000);
        });
        break;
    }
  };

  if (loading) {
    return <Loader label="Loading markets..." />;
  }

  return (
    <div className="markets-list">
      <div className="markets-header">
        <h2>
          Prediction Markets
          <Tooltip
            text="Browse all available prediction markets. Click on any market to view details and place orders."
            position="right"
          >
            <span className="tooltip-icon tooltip-icon-plain">
              <Info size={16} strokeWidth={2.25} />
            </span>
          </Tooltip>
        </h2>
        <div className="filter-buttons">
          <Tooltip
            text="Show all markets regardless of status"
            position="bottom"
          >
            <button
              className={filter === "all" ? "active" : ""}
              onClick={() => setFilter("all")}
            >
              All
            </button>
          </Tooltip>
          <Tooltip
            text="Show only markets that are still open for trading"
            position="bottom"
          >
            <button
              className={filter === "active" ? "active" : ""}
              onClick={() => setFilter("active")}
            >
              Active
            </button>
          </Tooltip>
          <Tooltip
            text="Show only markets that have been resolved with final outcomes"
            position="bottom"
          >
            <button
              className={filter === "resolved" ? "active" : ""}
              onClick={() => setFilter("resolved")}
            >
              Resolved
            </button>
          </Tooltip>
          <Tooltip
            text="Show only markets you've added to your watchlist"
            position="bottom"
          >
            <button
              className={filter === "watchlist" ? "active" : ""}
              onClick={() => setFilter("watchlist")}
            >
              Watchlist ({watchlist.length})
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="markets-filters">
        <div className="search-box">
          <div className="search-input-shell">
            <Search className="search-input-icon" />
            <input
              type="text"
              placeholder="Search markets..."
              value={searchQuery}
              onChange={(e) =>
                setSearchQuery(e.target.value.replace(/^\s+/, "").slice(0, 20))
              }
              maxLength={20}
              className="search-input"
            />
          </div>
        </div>
        <div className="filter-group">
          <FilterDropdown
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categoryOptions}
            className="category-select"
            ariaLabel="Filter markets by category"
          />
        </div>
        <div className="filter-group">
          <FilterDropdown
            value={sortBy}
            onChange={(value) =>
              setSortBy(value as "newest" | "expiry" | "popular")
            }
            options={sortOptions}
            className="sort-select"
            ariaLabel="Sort markets"
          />
        </div>
      </div>

      <div className="markets-grid">
        {filteredMarkets.length === 0 ? (
          <NoMarketsFound />
        ) : (
          filteredMarkets.map((market) => (
            <MarketsCard
              key={market.id}
              market={market}
              nowSec={nowSec}
              session={session}
              watchlist={watchlist}
              copiedMarketId={copiedMarketId}
              onSelectMarket={onSelectMarket}
              onToggleWatchlist={toggleWatchlist}
              onShare={handleShare}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default MarketsList;
